import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { MATIX_LOGO_DATA_URI } from './logo';
import { SISMATIX_MARK_MASK_URI } from './sismatix-logo';

/**
 * The shared Matix sign-in "ticket".
 *
 * One screen, four apps (admin, cashier, service, appointments). Each app
 * keeps its own auth store, its own multi-tenant flow and its own error
 * vocabulary — this component owns nothing but presentation and the two bits
 * of local state that are purely visual (password visibility, the printed
 * date stamp).
 *
 * Consumers must import the stylesheet once at app entry:
 *   import '@matix/ui/login-styles';
 *
 * `TicketShell` is exported separately so the post-login picker stages
 * (subsidiary → branch → drawer) can sit on the same paper instead of
 * dropping the operator onto a differently-styled screen mid-flow.
 */

export type TicketLang = 'en' | 'ar';

/** Every user-visible string comes in both languages, chosen by `lang`. */
export interface Bilingual {
  en: string;
  ar: string;
}

function pick(v: Bilingual, lang: TicketLang): string {
  return lang === 'ar' ? v.ar : v.en;
}

// ──────────────────────────────────────────────────────────────────── shell

export interface TicketShellProps {
  lang: TicketLang;
  onLangChange: (lang: TicketLang) => void;
  /** The small line above the rule — e.g. "Admin console · Powered by MatixERP". */
  console: Bilingual;
  /** Extra icon buttons beside the language toggle (the cashier's theme switch). */
  extraControls?: ReactNode;
  version?: string;
  supportEmail?: string;
  /** The company that owns the product. Shown in the copyright line. */
  vendorName?: string;
  vendorUrl?: string;
  children: ReactNode;
}

export function TicketShell({
  lang,
  onLangChange,
  console: consoleLabel,
  extraControls,
  version = 'v1.0',
  supportEmail = 'support@matix.one',
  vendorName = 'Sismatix',
  vendorUrl = 'https://sismatix.com',
  children,
}: TicketShellProps): JSX.Element {
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const [stamp, setStamp] = useState('');

  // Printed like a receipt's date line. Computed in an effect rather than at
  // module scope so a tab left open overnight doesn't show yesterday.
  useEffect(() => {
    const loc = lang === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB';
    setStamp(
      new Date().toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' }),
    );
  }, [lang]);

  const year = new Date().getFullYear();

  return (
    <div className="tkt-root" dir={dir} lang={lang}>
      <div className="tkt-shadow">
        <main className="tkt-ticket">
          <div className="tkt-top">
            <img className="tkt-logo" src={MATIX_LOGO_DATA_URI} alt="Matix POS" />
            {extraControls && <div className="tkt-extra">{extraControls}</div>}
            <div className="tkt-lang" role="group" aria-label="Language">
              <button
                type="button"
                aria-pressed={lang === 'ar'}
                onClick={() => onLangChange('ar')}
              >
                ع
              </button>
              <button
                type="button"
                aria-pressed={lang === 'en'}
                onClick={() => onLangChange('en')}
              >
                EN
              </button>
            </div>
          </div>

          <div className="tkt-meta">
            <span>{pick(consoleLabel, lang)}</span>
            <span>{stamp}</span>
          </div>

          <hr className="tkt-perf lead" />

          {children}

          <hr className="tkt-perf" />
          <div className="tkt-foot">
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            <span>{version}</span>
          </div>
          {/* Ownership line. The year is computed once at render rather than
              hard-coded, so nobody has to remember to bump it in January. */}
          <div className="tkt-legal">
            <a
              className="tkt-vendor"
              href={vendorUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {/* The mark is a CSS mask, so it takes its colour from the
                  stylesheet and turns accent on hover along with the word.
                  Decorative: the link is already named by the text beside it. */}
              <span
                className="tkt-vendor-mark"
                style={{ ['--tkt-vendor-mask' as string]: `url(${SISMATIX_MARK_MASK_URI})` }}
                aria-hidden="true"
              />
              {vendorName}
            </a>
            <span className="tkt-copy">
              {pick(
                {
                  en: `© ${year} ${vendorName}. All rights reserved.`,
                  ar: `© ${year} ${vendorName}. جميع الحقوق محفوظة.`,
                },
                lang,
              )}
            </span>
          </div>
        </main>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────── login

export interface TicketLoginValues {
  tenant: string;
  username: string;
  password: string;
  remember: boolean;
}

export interface TicketLoginProps extends Omit<TicketShellProps, 'children'> {
  title?: Bilingual;
  /** Apps call the tenant different things: "Domain", "Company", "Workspace". */
  tenantLabel?: Bilingual;
  rememberLabel?: Bilingual;
  values: TicketLoginValues;
  onChange: (patch: Partial<TicketLoginValues>) => void;
  onSubmit: () => void;
  busy?: boolean;
  /** Amber strip — context for why the operator is back here (session expiry). */
  banner?: string | null;
  /** Red strip — the last attempt failed. */
  error?: string | null;
  fieldErrors?: Partial<Record<'tenant' | 'username' | 'password', string>>;
  showRemember?: boolean;
  /** Hides the tenant field for apps that resolve the tenant from the host. */
  showTenant?: boolean;
  /**
   * Hides the username field. The till signs in on company + password alone,
   * because a password is unique within a tenant and one less field at the
   * counter is one less thing to mistype. The server then identifies the
   * cashier from the password — see `findUserByPassword`. Every other app
   * (admin, KDS, appointments) leaves this on.
   */
  showUsername?: boolean;
  /**
   * Rendered under the sign-in button. Optional and undefined by default, so
   * the apps that do not pass it render exactly as before. The till uses it
   * for «الدخول بالبصمة» inside the phone shell.
   */
  extra?: ReactNode;
}

const DEFAULT_TITLE: Bilingual = { en: 'Sign in', ar: 'تسجيل الدخول' };
const DEFAULT_TENANT: Bilingual = { en: 'Company', ar: 'الشركة' };
const DEFAULT_REMEMBER: Bilingual = {
  en: 'Remember this company on this device',
  ar: 'تذكّر هذه الشركة على هذا الجهاز',
};
const USERNAME: Bilingual = { en: 'Username', ar: 'اسم المستخدم' };
const PASSWORD: Bilingual = { en: 'Password', ar: 'كلمة المرور' };
const SIGN_IN: Bilingual = { en: 'Sign in', ar: 'تسجيل الدخول' };
const SIGNING_IN: Bilingual = { en: 'Signing in…', ar: 'جارٍ تسجيل الدخول…' };
const SHOW_PWD: Bilingual = { en: 'Show password', ar: 'إظهار كلمة المرور' };
const HIDE_PWD: Bilingual = { en: 'Hide password', ar: 'إخفاء كلمة المرور' };

export function TicketLogin({
  extra,
  title = DEFAULT_TITLE,
  tenantLabel = DEFAULT_TENANT,
  rememberLabel = DEFAULT_REMEMBER,
  values,
  onChange,
  onSubmit,
  busy = false,
  banner = null,
  error = null,
  fieldErrors = {},
  showRemember = true,
  showTenant = true,
  showUsername = true,
  ...shell
}: TicketLoginProps): JSX.Element {
  const { lang } = shell;
  const [showPwd, setShowPwd] = useState(false);

  const canSubmit =
    !busy &&
    (!showTenant || values.tenant.trim().length > 0) &&
    (!showUsername || values.username.trim().length > 0) &&
    values.password.length > 0;

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit();
  }

  return (
    <TicketShell {...shell}>
      <div className="tkt-reveal" style={{ animationDelay: '0.25s' }}>
        <h1>{pick(title, lang)}</h1>
      </div>

      <form className="tkt-reveal" style={{ animationDelay: '0.35s' }} onSubmit={handleSubmit} noValidate>
        {showTenant && (
          <div className="tkt-field">
            <label className="tkt-label" htmlFor="tkt-tenant">
              {pick(tenantLabel, lang)}
            </label>
            <div className={`tkt-control ltr ${fieldErrors.tenant ? 'invalid' : ''}`}>
              <input
                id="tkt-tenant"
                type="text"
                spellCheck={false}
                autoComplete="organization"
                autoCapitalize="none"
                autoCorrect="off"
                value={values.tenant}
                disabled={busy}
                aria-invalid={Boolean(fieldErrors.tenant)}
                // The charset here MUST match what the server accepts —
                // TENANT_DB_NAME_PATTERN in config/database.ts is
                // /^[a-zA-Z0-9_-]+$/ — and nothing narrower.
                //
                // It used to be `.toLowerCase().replace(/[^a-z0-9-]/g, '')`,
                // which silently ate two things real tenants have:
                //   * the UNDERSCORE — "villashams_test" was typed correctly and
                //     became "villashamstest", which resolves to nothing;
                //   * the CASE — "Cottoniltest" became "cottoniltest".
                // Both produced "your login details are not correct" against a
                // perfectly correct login, with the field showing what the
                // operator thought they had typed.
                //
                // Stripping the genuinely impossible characters as they type is
                // still worth doing — a space or a slash can only be a slip, and
                // catching it here beats a round trip — but the set of
                // impossible characters is exactly the server's.
                onChange={(e) =>
                  onChange({ tenant: e.target.value.replace(/[^A-Za-z0-9_-]/g, '') })
                }
                autoFocus={values.tenant.length === 0}
              />
            </div>
            {fieldErrors.tenant && <div className="tkt-field-error">{fieldErrors.tenant}</div>}
          </div>
        )}

        {showUsername && (
        <div className="tkt-field">
          <label className="tkt-label" htmlFor="tkt-user">
            {pick(USERNAME, lang)}
          </label>
          <div className={`tkt-control ltr ${fieldErrors.username ? 'invalid' : ''}`}>
            <input
              id="tkt-user"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={values.username}
              disabled={busy}
              aria-invalid={Boolean(fieldErrors.username)}
              onChange={(e) => onChange({ username: e.target.value })}
              autoFocus={showTenant && values.tenant.length > 0}
            />
          </div>
          {fieldErrors.username && <div className="tkt-field-error">{fieldErrors.username}</div>}
        </div>
        )}

        <div className="tkt-field">
          <label className="tkt-label" htmlFor="tkt-pass">
            {pick(PASSWORD, lang)}
          </label>
          <div className={`tkt-control ltr ${fieldErrors.password ? 'invalid' : ''}`}>
            <input
              id="tkt-pass"
              type={showPwd ? 'text' : 'password'}
              autoComplete="current-password"
              value={values.password}
              disabled={busy}
              autoFocus={!showUsername && (!showTenant || values.tenant.length > 0)}
              aria-invalid={Boolean(fieldErrors.password)}
              onChange={(e) => onChange({ password: e.target.value })}
            />
            <button
              type="button"
              className="tkt-peek"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={pick(showPwd ? HIDE_PWD : SHOW_PWD, lang)}
              title={pick(showPwd ? HIDE_PWD : SHOW_PWD, lang)}
            >
              {showPwd ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {fieldErrors.password && <div className="tkt-field-error">{fieldErrors.password}</div>}
        </div>

        {showRemember && showTenant && (
          <label className="tkt-remember">
            <input
              type="checkbox"
              checked={values.remember}
              disabled={busy}
              onChange={(e) => onChange({ remember: e.target.checked })}
            />
            <span>{pick(rememberLabel, lang)}</span>
          </label>
        )}

        {banner && (
          <div className="tkt-notice" role="status">
            <InfoIcon />
            <span>{banner}</span>
          </div>
        )}
        {error && (
          <div className="tkt-notice error" role="alert">
            <InfoIcon />
            <span>{error}</span>
          </div>
        )}

        <button className="tkt-submit" type="submit" disabled={!canSubmit}>
          <span>{pick(busy ? SIGNING_IN : SIGN_IN, lang)}</span>
          <ArrowIcon />
        </button>
        {extra}
      </form>
    </TicketShell>
  );
}

// ─────────────────────────────────────────────────────────────── stage list

export interface TicketStageOption {
  id: number | string;
  label: string;
  sub?: string | undefined;
}

/**
 * A pick-one step on the same paper — subsidiary, branch, till. Used after
 * the credential form so the operator never leaves the ticket mid-flow.
 */
export function TicketStage({
  title,
  options,
  onPick,
  onBack,
  busy = false,
  backLabel,
  emptyLabel,
  lang,
}: {
  title: string;
  options: TicketStageOption[];
  onPick: (id: number | string) => void;
  onBack?: (() => void) | undefined;
  busy?: boolean;
  backLabel?: string;
  emptyLabel?: string;
  lang: TicketLang;
}): JSX.Element {
  return (
    <div className="tkt-reveal">
      <div className="tkt-stage-head">
        <h1>{title}</h1>
        {onBack && (
          <button type="button" className="tkt-back" onClick={onBack} disabled={busy}>
            {backLabel ?? pick({ en: '← Back', ar: 'رجوع →' }, lang)}
          </button>
        )}
      </div>
      <div className="tkt-options">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            className="tkt-option"
            disabled={busy}
            onClick={() => onPick(o.id)}
          >
            <span>{o.label}</span>
            {o.sub && <span className="tkt-option-sub">{o.sub}</span>}
          </button>
        ))}
        {options.length === 0 && (
          <div className="tkt-field-error" style={{ textAlign: 'center', padding: '18px 0' }}>
            {emptyLabel ?? pick({ en: 'No options available.', ar: 'لا توجد خيارات متاحة.' }, lang)}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────── icons

function EyeIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.06 10.06 0 0 1 12 19c-7 0-10-7-10-7a17.43 17.43 0 0 1 4.06-5.06" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a17.31 17.31 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

function InfoIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4.5" />
      <path d="M12 16h.01" />
    </svg>
  );
}

function ArrowIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}
